import {useInfiniteQuery, useQuery, type QueryClient} from "@tanstack/react-query";
import {
	getImproveAPI,
	LeaderboardsReadInternalControllerGetUserLeaderboardParams,
	LeaderboardsReadInternalControllerListCohortLeaderboardParams,
	ListAwardEventsResponseDto,
	ListCohortLeaderboardResponseDto,
	UserLeaderboardResponseDto,
	type GetMeResponse
} from "../gen";
import {useImproveAxios} from "../../hooks/useImproveAxios";
import {QUERY_KEYS} from "./user";

export const LEADERBOARDS_QUERY_KEYS = {
	USER_LEADERBOARD: "userLeaderboard",
	COHORT_LEADERBOARD: "cohortLeaderboard",
	AWARD_EVENTS: "awardEvents"
} as const;

const DEFAULT_COHORT_LEADERBOARD_PAGE = 1;
const DEFAULT_COHORT_LEADERBOARD_PAGE_SIZE = 200;

/** Page size for learner leaderboard modal infinite scroll. */
export const LEADERBOARD_MODAL_PAGE_SIZE = 25;

type LeaderboardSocketUpdate = {
	cohortId: number;
	newRank?: number;
	totalPoints?: number;
	isPositiveRank?: boolean;
	leaderboardPeriodId?: number;
};

const socketNumber = (raw: Record<string, unknown>, key: string): number | undefined => {
	const value = raw[key];
	if (value == null || value === "") {
		return undefined;
	}
	const n = Number(value);
	return Number.isFinite(n) ? n : undefined;
};

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
	value != null && typeof value === "object" && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: undefined;

const hasCohortId = (record: Record<string, unknown>): boolean =>
	socketNumber(record, "cohortId") != null || socketNumber(record, "cohort_id") != null;

/**
 * Walks nested `data` / `leaderboardUpdate` wrappers (Socket.IO, Bull, double-wrapped emits).
 */
const findLeaderboardDtoRecord = (event: unknown, depth = 0): Record<string, unknown> | undefined => {
	if (depth > 8 || event == null) {
		return undefined;
	}

	let current: unknown = event;
	if (typeof current === "string") {
		try {
			current = JSON.parse(current) as unknown;
		} catch {
			return undefined;
		}
	}

	const record = asRecord(current);
	if (!record) {
		return undefined;
	}

	if (hasCohortId(record)) {
		return record;
	}

	// `{ data: … }` — may be nested more than once
	if (record.data != null) {
		const fromData = findLeaderboardDtoRecord(record.data, depth + 1);
		if (fromData) {
			return fromData;
		}
	}

	// `{ leaderboardUpdate: … }` — Bull notification envelope
	if (record.leaderboardUpdate != null) {
		const fromUpdate = findLeaderboardDtoRecord(record.leaderboardUpdate, depth + 1);
		if (fromUpdate) {
			return fromUpdate;
		}
	}

	return undefined;
};

const parseLeaderboardSocketEvent = (event: unknown): LeaderboardSocketUpdate | undefined => {
	const raw = findLeaderboardDtoRecord(event);
	if (!raw) {
		return undefined;
	}

	const cohortId = socketNumber(raw, "cohortId") ?? socketNumber(raw, "cohort_id");
	if (cohortId == null) {
		return undefined;
	}

	return {
		cohortId,
		leaderboardPeriodId:
			socketNumber(raw, "leaderboardPeriodId") ?? socketNumber(raw, "leaderboard_period_id"),
		newRank: socketNumber(raw, "newRank") ?? socketNumber(raw, "new_rank"),
		totalPoints: socketNumber(raw, "totalPoints") ?? socketNumber(raw, "total_points"),
		isPositiveRank:
			raw.isPositiveRank === true ||
			raw.isPositiveRank === "true" ||
			raw.is_positive_rank === true ||
			raw.is_positive_rank === "true"
	};
};

const matchesCohortLeaderboardQueryKey = (key: readonly unknown[], cohortId: number): boolean => {
	if (key[0] !== LEADERBOARDS_QUERY_KEYS.COHORT_LEADERBOARD) {
		return false;
	}
	return key.some((part) => part == cohortId);
};

const matchesUserLeaderboardQueryKey = (key: readonly unknown[], cohortId: number): boolean => {
	if (key[0] !== LEADERBOARDS_QUERY_KEYS.USER_LEADERBOARD) {
		return false;
	}
	const params = key[2];
	if (params == null || typeof params !== "object") {
		return true;
	}
	const keyCohortId = (params as {cohortId?: number}).cohortId;
	return keyCohortId == null || keyCohortId == cohortId;
};

const patchUserLeaderboardFromSocket = (
	queryClient: QueryClient,
	update: LeaderboardSocketUpdate
): void => {
	const {cohortId, newRank, totalPoints, isPositiveRank, leaderboardPeriodId} = update;
	if (newRank == null || totalPoints == null) {
		return;
	}
	queryClient.setQueriesData<UserLeaderboardResponseDto>(
		{
			predicate: (query) => matchesUserLeaderboardQueryKey(query.queryKey, cohortId)
		},
		(old) => {
			if (!old) {
				return old;
			}
			return {
				...old,
				cohortId,
				...(leaderboardPeriodId != null ? {leaderboardPeriodId} : {}),
				rank: newRank,
				totalPoints,
				isPositiveRank: isPositiveRank ?? old.isPositiveRank
			};
		}
	);
};

const resolveLeaderboardCohortId = (queryClient: QueryClient, event: unknown): number | undefined => {
	const parsed = parseLeaderboardSocketEvent(event);
	if (parsed) {
		return parsed.cohortId;
	}
	return queryClient.getQueryData<GetMeResponse>([QUERY_KEYS.ME])?.memberedCohort?.id;
};

/**
 * Invalidates leaderboard React Query caches after Socket.IO `leaderboardUpdated`.
 */
export const invalidateLeaderboardQueries = (queryClient: QueryClient, event: unknown): void => {
	const cohortId = resolveLeaderboardCohortId(queryClient, event);
	if (cohortId == null) {
		return;
	}

	const update = parseLeaderboardSocketEvent(event);
	if (update) {
		patchUserLeaderboardFromSocket(queryClient, update);
	}

	queryClient.invalidateQueries({
		queryKey: [LEADERBOARDS_QUERY_KEYS.USER_LEADERBOARD],
		predicate: (query) => matchesUserLeaderboardQueryKey(query.queryKey, cohortId)
	});
	queryClient.invalidateQueries({
		predicate: (query) => matchesCohortLeaderboardQueryKey(query.queryKey, cohortId)
	});
};

/**
 * Current-period leaderboard row for a user (`GET /api/_internal/leaderboards/:userId/user`).
 */
export const useGetUserLeaderboard = (
	userId: number | undefined,
	params?: LeaderboardsReadInternalControllerGetUserLeaderboardParams,
	options?: {enabled?: boolean}
) => {
	const axiosConfig = useImproveAxios();
	const {leaderboardsReadInternalControllerGetUserLeaderboard} = getImproveAPI();

	return useQuery<UserLeaderboardResponseDto>({
		queryKey: [LEADERBOARDS_QUERY_KEYS.USER_LEADERBOARD, userId, params],
		queryFn: async () => {
			const response = await leaderboardsReadInternalControllerGetUserLeaderboard(
				userId!,
				params,
				axiosConfig
			);
			return response.data;
		},
		enabled: options?.enabled !== false && userId != null,
		staleTime: 5 * 60 * 1000
	});
};

/**
 * Paginated cohort leaderboard (`GET /api/_internal/leaderboards/:cohortId/cohort`).
 */
export const useListCohortLeaderboard = (
	cohortId: number | undefined,
	params?: LeaderboardsReadInternalControllerListCohortLeaderboardParams,
	options?: {enabled?: boolean}
) => {
	const axiosConfig = useImproveAxios();
	const {leaderboardsReadInternalControllerListCohortLeaderboard} = getImproveAPI();

	const page = params?.page ?? DEFAULT_COHORT_LEADERBOARD_PAGE;
	const pageSize = params?.pageSize ?? DEFAULT_COHORT_LEADERBOARD_PAGE_SIZE;
	const dateFrom = params?.dateFrom;
	const dateTo = params?.dateTo;

	const requestParams: LeaderboardsReadInternalControllerListCohortLeaderboardParams = {
		page,
		pageSize,
		...(dateFrom != null ? {dateFrom} : {}),
		...(dateTo != null ? {dateTo} : {})
	};

	return useQuery<ListCohortLeaderboardResponseDto>({
		queryKey: [
			LEADERBOARDS_QUERY_KEYS.COHORT_LEADERBOARD,
			cohortId,
			page,
			pageSize,
			dateFrom,
			dateTo
		],
		queryFn: async () => {
			const response = await leaderboardsReadInternalControllerListCohortLeaderboard(
				cohortId!,
				requestParams,
				axiosConfig
			);
			return response.data;
		},
		enabled: options?.enabled !== false && cohortId != null,
		staleTime: 5 * 60 * 1000
	});
};

export type UseListCohortLeaderboardInfiniteParams = Omit<
	LeaderboardsReadInternalControllerListCohortLeaderboardParams,
	"page" | "pageSize"
> & {
	pageSize?: number;
};

/**
 * Infinite cohort leaderboard for modal lists (`pageSize` defaults to 25).
 */
export const useListCohortLeaderboardInfinite = (
	cohortId: number | undefined,
	params?: UseListCohortLeaderboardInfiniteParams,
	options?: {enabled?: boolean}
) => {
	const axiosConfig = useImproveAxios();
	const {leaderboardsReadInternalControllerListCohortLeaderboard} = getImproveAPI();

	const pageSize = params?.pageSize ?? LEADERBOARD_MODAL_PAGE_SIZE;
	const dateFrom = params?.dateFrom;
	const dateTo = params?.dateTo;

	const requestBase: Omit<LeaderboardsReadInternalControllerListCohortLeaderboardParams, "page"> = {
		pageSize,
		...(dateFrom != null ? {dateFrom} : {}),
		...(dateTo != null ? {dateTo} : {})
	};

	return useInfiniteQuery<ListCohortLeaderboardResponseDto>({
		queryKey: [
			LEADERBOARDS_QUERY_KEYS.COHORT_LEADERBOARD,
			"infinite",
			cohortId,
			pageSize,
			dateFrom,
			dateTo
		],
		queryFn: async ({pageParam = 1}) => {
			const response = await leaderboardsReadInternalControllerListCohortLeaderboard(
				cohortId!,
				{...requestBase, page: pageParam as number},
				axiosConfig
			);
			return response.data;
		},
		getNextPageParam: (lastPage, allPages) => {
			const loaded = allPages.reduce((sum, page) => sum + page.items.length, 0);
			return lastPage.totalCount > loaded ? allPages.length + 1 : undefined;
		},
		initialPageParam: 1,
		enabled: options?.enabled !== false && cohortId != null,
		staleTime: 5 * 60 * 1000
	});
};

/**
 * Active award event definitions (`GET /api/_internal/leaderboards/award-events`).
 */
export const useListAwardEvents = (options?: {enabled?: boolean}) => {
	const axiosConfig = useImproveAxios();
	const {awardEventsInternalControllerList} = getImproveAPI();

	return useQuery<ListAwardEventsResponseDto>({
		queryKey: [LEADERBOARDS_QUERY_KEYS.AWARD_EVENTS, axiosConfig.baseURL],
		queryFn: async () => {
			const response = await awardEventsInternalControllerList(axiosConfig);
			return response.data;
		},
		enabled: options?.enabled !== false,
		staleTime: 30 * 60 * 1000
	});
};
